export type ScoringMode = 'sum' | 'average' | 'transformed' | 'custom';
export type LevelRule = (percent: number, raw: number) => 'High' | 'Moderate' | 'Low';

export interface ScoringConfigEntry {
  max: number;
  mode: ScoringMode;
  levelRule?: LevelRule;
}

export const defaultLevelRule: LevelRule = (percent: number) => (percent >= 75 ? 'High' : percent >= 50 ? 'Moderate' : 'Low');

export const scoringConfig: Record<string, ScoringConfigEntry> = {
  'dass-21': { 
    max: 42, 
    mode: 'sum',
    levelRule: (percent: number, raw: number) => {
      // For DASS-21, the level is determined by the highest subscale score
      // This is handled in computeScore.ts where raw = max(subscale scores)
      if (raw <= 14) return 'Low';
      else if (raw <= 25) return 'Moderate';
      else return 'High';
    }
  },
  'phq-9': { max: 27, mode: 'sum' },
  'gad-7': { max: 21, mode: 'sum' },
  gender: { max: 20, mode: 'sum' },
  'legendarial-swls': { max: 35, mode: 'sum' },
  'legendarial-pil': { max: 70, mode: 'sum' },
  'religio-brief-rcope': {
    max: 56,
    mode: 'sum',
    levelRule: (percent: number, raw: number) => {
      // Fix: 100% (56/56) should be High, not Moderate
      if (raw === 56) return 'High';
      if (raw >= 38) return 'High';      // 38/56 (68%) and above = High
      else if (raw >= 32) return 'High'; // 32/56 (57%) = High
      else if (raw >= 35) return 'Low';   // 35/56 (63%) = Low
      else return 'Low';                  // Below 35 = Low
    }
  },
  'religio-cvscale': {
    max: 5,
    mode: 'average',
    levelRule: (percent: number, raw: number) => {
      // CVSCALE: 1.0-2.4 = Low, 2.5-3.4 = Moderate, 3.5-5.0 = High
      if (raw >= 3.5) return 'High';
      else if (raw >= 2.5) return 'Moderate';
      else return 'Low';
    }
  },
  'religio-tvas': {
    max: 50,
    mode: 'sum',
    levelRule: (percent: number, raw: number) => {
      // TVAS: 10-24 = Low, 25-37 = Moderate, 38-50 = High
      if (raw >= 38) return 'High';
      else if (raw >= 25) return 'Moderate';
      else return 'Low';
    }
  },
  'socio-relational': { max: 20, mode: 'sum' },
  financial: { max: 80, mode: 'sum' },
  nutritional: { max: 100, mode: 'sum' },
  physical: { max: 100, mode: 'sum' },
  'physical-general': { max: 100, mode: 'sum' },
  'physical-ipaq': { max: 100, mode: 'sum' },
  'physical-psqi': { max: 100, mode: 'sum' },
  environmental: { max: 100, mode: 'transformed' },
  'occupational-cbi': { max: 100, mode: 'sum' },
  'occupational-uwes': { max: 6, mode: 'average' },
  'occupational-employee': {
    max: 5,
    mode: 'average',
    levelRule: (percent: number, raw: number) => {
      // High engagement (high score) = High
      if (raw >= 4) return 'High';      // 4-5: High engagement
      else if (raw >= 2.5) return 'Moderate'; // 2.5-3.9: Moderate
      else return 'Low';                // 1-2.4: Low engagement
    }
  },
  fasdl: {
    max: 50,
    mode: 'sum',
    levelRule: (percent: number, raw: number) => (raw >= 38 ? 'High' : raw >= 25 ? 'Moderate' : 'Low')
  },
  'facit-sp-12': {
    max: 48,
    mode: 'sum',
    levelRule: (percent: number, raw: number) => (raw >= 36 ? 'High' : raw >= 25 ? 'Moderate' : 'Low')
  },
};

